import React, { useEffect, useState } from 'react';
import {
  disableTextAlerts,
  enableTextAlerts,
  playChime,
  refreshAlertState,
  useTextAlertState,
  watchIncoming,
} from '../services/textAlerts';
import './TextAlerts.css';

/**
 * Text alerts on screen:
 *   <NewTextBanner>     slides in when a customer texts / emails while the app is open (+ a chime);
 *                       tap it → their thread
 *   <TextAlertsButton>  turn phone notifications on / off for this phone (Communications)
 */
const prettyPhone = (p) => {
  const d = String(p || '').replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '');
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : p || '';
};

export const NewTextBanner = ({ onOpen }) => {
  const [item, setItem] = useState(null);

  useEffect(
    () =>
      watchIncoming((list) => {
        const last = list[list.length - 1];
        setItem({ ...last, more: list.length - 1, id: Date.now() });
        playChime();
      }),
    []
  );

  // gone after 10 s
  useEffect(() => {
    if (!item) return undefined;
    const t = setTimeout(() => setItem(null), 10000);
    return () => clearTimeout(t);
  }, [item]);

  if (!item) return null;
  const who = item.name || prettyPhone(item.contact);
  return (
    <div className="new-text-banner" role="status">
      <button
        type="button"
        className="new-text-main"
        onClick={() => {
          onOpen({ key: item.contact, name: item.name, channel: item.channel });
          setItem(null);
        }}
      >
        <span className="new-text-icon">{item.channel === 'email' ? '✉️' : '💬'}</span>
        <span className="new-text-txt">
          <b>{who}{item.more > 0 ? ` +${item.more} more` : ''}</b>
          <span>{item.body}</span>
        </span>
      </button>
      <button type="button" className="new-text-close" onClick={() => setItem(null)} aria-label="Dismiss">✕</button>
    </div>
  );
};

export const TextAlertsButton = () => {
  const state = useTextAlertState();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    refreshAlertState();
  }, []);

  if (state === 'unsupported') return null;
  if (state === 'home-screen') {
    return <p className="alerts-hint">🔔 To get text alerts on this iPhone, open the app from its home-screen icon.</p>;
  }
  if (state === 'blocked') {
    return <p className="alerts-hint">🔕 Notifications are blocked for this app — allow them in the phone’s Settings to get text alerts.</p>;
  }
  const on = state === 'on';
  return (
    <div className="alerts-row">
      <button
        type="button"
        className={`alerts-btn${on ? ' on' : ''}`}
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setMsg('');
          try {
            if (on) await disableTextAlerts();
            else await enableTextAlerts();
          } catch (err) {
            setMsg(err.message);
          }
          setBusy(false);
        }}
      >
        {busy ? '…' : on ? '🔔 Text alerts on' : '🔔 Turn on text alerts'}
      </button>
      {msg && <span className="alerts-msg">{msg}</span>}
    </div>
  );
};
